/**
 * Session storage layer: the only place that writes companies/projects to session storage.
 * Pages call "ensure" / "refresh" here; they never call setCompaniesData directly.
 * - App load / Home: companies + all projects (no users).
 * - Project directory: that project + its users (merge into storage).
 * - Company directory: that company's projects + users (merge that company's wrapper).
 * - Refresh: current URL decides what to re-fetch and merge.
 */

import { fetchCompaniesAll, fetchCompanyUsers, fetchUsersForProject, loadProjects } from "./api";
import {
  getCompanies,
  getCompaniesData,
  getCompanyUsers,
  getProjectById,
  getProjectsForCompany,
  hasCompanyUsersInStorage,
  hasProjectUsersInStorage,
  setCompaniesData,
} from "./storage";

/** Convert project array to map keyed by project id. Enriches with company_id/company if missing. */
function projectsArrayToMap(arr, companyId, company) {
  const map = {};
  if (!Array.isArray(arr)) return map;
  arr.forEach((p) => {
    if (p != null && p.id != null) {
      const enriched = { ...p };
      if (enriched.company_id == null && enriched.company?.id == null && companyId != null) {
        enriched.company_id = companyId;
      }
      if (!enriched.company && company) {
        enriched.company = company;
      }
      map[String(p.id)] = enriched;
    }
  });
  return map;
}

/** Merge wrappers into storage by company id. Projects stored as { [projectId]: project }. */
function mergeProjectsIntoStorage(wrappers) {
  if (!Array.isArray(wrappers) || wrappers.length === 0) return;
  const data = getCompaniesData() || {};
  wrappers.forEach((w) => {
    if (w == null || w.company_id == null) return;
    const cid = Number(w.company_id);
    const key = String(cid);
    const entry = data[key] || {};
    data[key] = {
      ...entry,
      status: w.status ?? 200,
      error: w.error ?? null,
      projects: projectsArrayToMap(w.projects, cid, entry.company),
    };
  });
  setCompaniesData(data);
}

/** Upsert one project (with users) into the blob for companyId. Projects stored as { [projectId]: project }. */
function mergeProjectIntoStorage(companyId, projectWithUsers) {
  if (companyId == null || !projectWithUsers?.id) return;
  const cid = Number(companyId);
  const key = String(cid);
  const data = getCompaniesData() || {};
  const entry = data[key] || {};
  const existing = entry.projects;
  const map = existing && !Array.isArray(existing) ? { ...existing } : {};
  const enriched = { ...projectWithUsers };
  if (enriched.company_id == null && enriched.company?.id == null) {
    enriched.company_id = cid;
  }
  if (!enriched.company && entry.company) {
    enriched.company = entry.company;
  }
  map[String(projectWithUsers.id)] = enriched;
  data[key] = { ...entry, status: entry.status ?? 200, projects: map };
  setCompaniesData(data);
}

/**
 * Load companies and all projects (no users) into storage. Used on app load / Home.
 * When ignoreCache is false, uses session storage if we already have companies; only the REFRESH button passes true.
 */
export async function ensureCompaniesAndProjectsLoaded(ignoreCache = false) {
  const existing = getCompanies();
  if (!ignoreCache && existing && existing.length > 0) {
    return; // use session storage, don't hit backend
  }
  const companies = await fetchCompaniesAll(ignoreCache);
  const data = getCompaniesData() || {};
  companies.forEach((c) => {
    const key = String(c.id);
    data[key] = { ...data[key], company: c };
  });
  setCompaniesData(data);
  const wrappers = await loadProjects({ ignoreCache });
  if (Array.isArray(wrappers) && wrappers.length > 0) {
    mergeProjectsIntoStorage(wrappers);
  }
}

/**
 * Ensure the given project exists in storage. Optionally include users.
 * @param {number} companyId
 * @param {number} projectId
 * @param {boolean} [ignoreCache=false]
 * @param {boolean} [includeUsers=true]
 * @returns {Promise<object|null>} project or null
 */
export async function ensureProjectWithUsers(
  companyId,
  projectId,
  ignoreCache = false,
  includeUsers = true
) {
  const cid = Number(companyId);
  const pid = Number(projectId);

  const p = getProjectById(cid, pid);
  const needFetch = ignoreCache || !p || (includeUsers && !hasProjectUsersInStorage(cid, pid));
  if (!needFetch) {
    return includeUsers ? (p?.users != null ? p : null) : p;
  }

  const wrappers = await loadProjects({
    projectId: pid,
    companyId: cid,
    includeUsers,
    ignoreCache,
  });
  const wrapper = Array.isArray(wrappers) && wrappers.length > 0 ? wrappers[0] : null;
  if (wrapper?.status === 200 && Array.isArray(wrapper.projects) && wrapper.projects.length > 0) {
    const project = wrapper.projects[0];
    mergeProjectIntoStorage(wrapper.company_id, project);
    return project;
  }
  return null;
}

/**
 * Ensure the given company's projects (with users) are in storage. Fetches and merges that company's wrapper.
 * Returns the wrapper { company_id, projects } for that company.
 */
export async function ensureCompanyProjectsWithUsers(companyId, ignoreCache = false) {
  const cid = Number(companyId);
  const wrappers = await loadProjects({ companyId: cid, includeUsers: true, ignoreCache });
  const wrapper = Array.isArray(wrappers) && wrappers.length > 0 ? wrappers[0] : null;
  if (wrapper?.status === 200 && Array.isArray(wrapper.projects)) {
    mergeProjectsIntoStorage([wrapper]);
    return wrapper;
  }
  return wrapper;
}

/** Upsert the company-users array onto the per-company wrapper. */
function mergeCompanyUsersIntoStorage(companyId, users) {
  if (companyId == null || !Array.isArray(users)) return;
  const cid = Number(companyId);
  const key = String(cid);
  const data = getCompaniesData() || {};
  const entry = data[key] || {};
  data[key] = { ...entry, status: entry.status ?? 200, users };
  setCompaniesData(data);
}

/**
 * Ensure the company's users (active + inactive merged) are in storage.
 * Returns the users array. Hits the backend only on cache miss / ignoreCache.
 */
export async function ensureCompanyUsers(companyId, ignoreCache = false) {
  const cid = Number(companyId);
  if (!ignoreCache) {
    const existing = getCompanyUsers(cid);
    if (existing != null) return existing;
  }
  const users = await fetchCompanyUsers(cid, ignoreCache);
  mergeCompanyUsersIntoStorage(cid, users);
  return users;
}

/** Write-through helper for pages that fetched company users themselves. */
export function setCompanyUsersInStorage(companyId, users) {
  mergeCompanyUsersIntoStorage(companyId, users);
}

/**
 * Update users for one project in storage (e.g. after bulk add/remove). Only the layer writes storage.
 */
export function updateProjectUsersInStorage(companyId, projectId, users) {
  const cid = Number(companyId);
  const pid = Number(projectId);
  const p = getProjectById(cid, pid);
  if (!p) return;
  mergeProjectIntoStorage(cid, { ...p, users: Array.isArray(users) ? users : [] });
}

/**
 * Refresh project users in storage for all projects of a company that have users loaded.
 * Call this when real-time events indicate users were added/removed.
 */
export async function refreshProjectUsersForCompany(companyId, forceRefresh = false) {
  const cid = Number(companyId);
  const projects = getProjectsForCompany(cid);
  if (!projects?.length) return;

  const projectIds = projects
    .filter((p) => p && hasProjectUsersInStorage(cid, p.id))
    .map((p) => p.id)
    .filter((id) => id != null);
  if (!projectIds.length) return;

  const results = await Promise.allSettled(
    projectIds.map(async (pid) => {
      const users = await fetchUsersForProject(cid, pid, forceRefresh);
      return { pid, users };
    })
  );
  results.forEach((result) => {
    if (result.status === "fulfilled") {
      const { pid, users } = result.value;
      updateProjectUsersInStorage(cid, pid, users);
    }
  });
}

/**
 * Refresh based on current route: re-fetch and merge the right scope.
 * @param {string} pathname - e.g. useLocation().pathname
 */
export async function refreshForCurrentRoute(pathname) {
  if (!pathname) return;

  const projectMatch = pathname.match(/^\/project\/(\d+)\/(\d+)$/);
  if (projectMatch) {
    await ensureProjectWithUsers(projectMatch[1], projectMatch[2], true, true);
    return;
  }

  const forecastMatch = pathname.match(/^\/forecast\/(\d+)\/(\d+)$/);
  if (forecastMatch) {
    await ensureProjectWithUsers(forecastMatch[1], forecastMatch[2], true, false);
    return;
  }

  const changeMgmtMatch = pathname.match(/^\/change-management\/(\d+)\/(\d+)$/);
  if (changeMgmtMatch) {
    await ensureProjectWithUsers(changeMgmtMatch[1], changeMgmtMatch[2], true, false);
    return;
  }

  const companyMatch = pathname.match(/^\/company\/(\d+)$/);
  if (companyMatch) {
    await ensureCompanyProjectsWithUsers(companyMatch[1], true);
    return;
  }

  // Home or other: refresh companies + all projects (no users)
  if (pathname === "/home" || pathname === "/") {
    await ensureCompaniesAndProjectsLoaded(true);
  }
}

// Re-export read-only getters for pages (updateProjectUsersInStorage is exported inline above)
export {
  getCompanies,
  getCompaniesData,
  getCompanyUsers,
  getProjectById,
  getProjectsForCompany,
  hasCompanyUsersInStorage,
  hasProjectUsersInStorage,
} from "./storage";
